import { createSlice } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';
import { VendorSearchResponseDto,VendorV2Dto } from '@inosio/payments-sdk';

interface VendorsDataPage {
  Rows: Array<VendorV2Dto>;
}

type VendorState = {
  Vendors: VendorSearchResponseDto;
  VendorsDataPages: Array<VendorsDataPage>;
  isFetchingVendors: Boolean;
  isFetchingVendorsFailed: Boolean;
  isFetchingVendorsSuccess: Boolean;
  errorMessage: ErrorType;
  isCreatingVendor: Boolean;
  isCreatingVendorSuccess: Boolean;
  isCreatingVendorFailed: Boolean;
  isUpdatingVendor: Boolean;
  isUpdatingVendorSuccess: Boolean;
  isUpdatingVendorFailed: Boolean;
  isDeletingVendorUser: Boolean;
  isDeletingVendorUserSuccess: Boolean;
  isDeletingVendorUserFailed: Boolean;
};

const getInitialState = () => {
  return {
    Vendors: { TotalCount: 0, RecordStart: 0, RangeSize: 100, Rows: [] },
    VendorsDataPages: [],
    isFetchingVendors: false,
    isFetchingVendorsFailed: false,
    isFetchingVendorsSuccess: false,
    errorMessage: {} as ErrorType,
    isCreatingVendor: false,
    isCreatingVendorSuccess: false,
    isCreatingVendorFailed: false,
    createdVendor: {} as VendorV2Dto,
    isUpdatingVendor: false,
    isUpdatingVendorSuccess: false,
    isUpdatingVendorFailed: false,
    isDeletingVendor: false,
    isDeletingVendorSuccess: false,
    isDeletingVendorFailed: false,
    isDeletingVendorUser: false,
    isDeletingVendorUserSuccess: false,
    isDeletingVendorUserFailed: false,
  } as VendorState;
};

const VendorsSlice = createSlice({
  name: 'Vendors',
  initialState: getInitialState(),
  reducers: {
    getFetchingVendorsData(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isFetchingVendorsFailed = false;
      state.isFetchingVendors = true;
    },
    setFetchingVendorsData(state, action) {
      const _payload = action.payload;
      let currentVendorPages = [...state.VendorsDataPages];
      if (_payload.RecordStart === 0) currentVendorPages = [];

      const whatPageResultsIsThis = Math.ceil(_payload.RecordStart / _payload.RangeSize);
      currentVendorPages[whatPageResultsIsThis] = {
        Rows: _payload.Rows,
      };
      const vendorsData = {
        Vendors: action.payload,
        VendorsDataPages: currentVendorPages,
        isFetchingVendors: false,
        isFetchingVendorsFailed: false,
        isFetchingVendorsSuccess: true,
      };
      return { ...state, ...vendorsData };
    },
    getFetchingVendorsDataFailed(state, action) {
      state.isFetchingVendorsFailed = true;
      state.isFetchingVendors = false;
      state.errorMessage = action.payload;
      state.isFetchingVendorsSuccess = false;
    },
    getCreatingVendor(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isCreatingVendorFailed = false;
      state.isCreatingVendorSuccess = false;
      state.isCreatingVendor = true;
    },
    setCreatingVendor(state) {
      state.isCreatingVendorFailed = false;
      state.isCreatingVendorSuccess = true;
      state.isCreatingVendor = false;
    },
    getCreatingVendorFailed(state, action) {
      state.isCreatingVendorFailed = true;
      state.isCreatingVendorSuccess = false;
      state.isCreatingVendor = false;
      state.errorMessage = action.payload;
    },
    getUpdatingVendor(state, action) {
      state.errorMessage = {} as ErrorType;
      state.isUpdatingVendorSuccess = false;
      state.isUpdatingVendorFailed = false;
      state.isUpdatingVendor = true;
    },
    setUpdatingVendor(state) {
      state.isUpdatingVendorSuccess = true;
      state.isUpdatingVendorFailed = false;
      state.isUpdatingVendor = false;
    },
    getUpdatingVendorFailed(state, action) {
      state.isUpdatingVendorSuccess = false;
      state.isUpdatingVendorFailed = true;
      state.isUpdatingVendor = false;
      state.errorMessage = action.payload;
    },
    deleteVendorUser(state, action) {
      state.isDeletingVendorUser = true;
      state.isDeletingVendorUserSuccess = false;
      state.isDeletingVendorUserFailed = false;
    },
    handleDeleteVendorUserSuccess(state, action) {
      state.isDeletingVendorUser = false;
      state.isDeletingVendorUserSuccess = true;
      state.isDeletingVendorUserFailed = false;
    },
    handleDeleteVendorUserFailed(state, action) {
      state.isDeletingVendorUser = false;
      state.isDeletingVendorUserSuccess = false;
      state.isDeletingVendorUserFailed = true;
      state.errorMessage = action.payload;
    },
    getResetVendorsState(state) {
      return {
        ...state,
        isFetchingVendors: false,
        isFetchingVendorsFailed: false,
        isFetchingVendorsSuccess: false,
        isCreatingVendor: false,
        isCreatingVendorSuccess: false,
        isCreatingVendorFailed: false,
        isUpdatingVendor: false,
        isUpdatingVendorSuccess: false,
        isUpdatingVendorFailed: false,
        isFetchingVendorUsers: false,
        isFetchingVendorUsersFailed: false,
        isFetchingVendorUsersSuccess: false,
        isDeletingVendorUser: false,
        isDeletingVendorUserSuccess: false,
        isDeletingVendorUserFailed: false,
        errorMessage: {} as ErrorType,
      };
    },
  },
});

export const {
  getFetchingVendorsData,
  setFetchingVendorsData,
  getFetchingVendorsDataFailed,
  getCreatingVendor,
  setCreatingVendor,
  getCreatingVendorFailed,
  getUpdatingVendor,
  setUpdatingVendor,
  getUpdatingVendorFailed,
  deleteVendorUser,
  handleDeleteVendorUserSuccess,
  handleDeleteVendorUserFailed,
  getResetVendorsState,
} = VendorsSlice.actions;

export default VendorsSlice.reducer;
export const selectVendors = (state: RootState) => state.vendorsState.Vendors;
export const selectVendorsCurrentPage = (state: RootState) => state.vendorsState.VendorsDataPages;
export const selectVendorsState = (state: RootState) => state.vendorsState;
